import { useEffect, useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { History as HistoryIcon } from 'lucide-react'
import { PageShell } from '../../layout/PageShell'
import { PageBrow } from '../../layout/PageBrow'
import { ListDetail } from '../../layout/ListDetail'
import { EmptyState } from '../../patterns/EmptyState'
import { historyApi, channelApi } from '../../../services/api'
import type { HistorySessionListItem, HistorySessionDetail } from '../../../services/historyTypes'
import SessionList from './SessionList'
import SessionDetail from './SessionDetail'
import { pickSessionForRange } from './pickSessionForRange'

export default function VoiceSessionHistoryPage() {
  const { channelId = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const selectedId = params.get('s')
  const [list, setList] = useState<HistorySessionListItem[]>([])
  const [detail, setDetail] = useState<HistorySessionDetail | null>(null)
  const [denied, setDenied] = useState(false)
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState<{ tagId?: string; pinned?: boolean; q?: string }>({})
  const [isHost, setIsHost] = useState(false)
  const [vocab, setVocab] = useState<{ id: string; label: string; color: string | null }[]>([])
  const [refreshTick, setRefreshTick] = useState(0)

  useEffect(() => {
    setLoading(true); setDenied(false)
    historyApi.list(channelId)
      .then((r) => setList(r.data))
      .catch((e) => { if (e?.response?.status === 403) setDenied(true) })
      .finally(() => setLoading(false))
  }, [channelId])

  useEffect(() => {
    if (selectedId || loading || list.length === 0) return
    const from = params.get('from')
    const to = params.get('to')
    const pick = pickSessionForRange(list, from, to)
    if (pick) setParams({ s: pick })
  }, [loading, list, selectedId, params, setParams])

  useEffect(() => {
    channelApi.hostStatus(channelId).then((r: any) => setIsHost(!!r.data.isHost)).catch(() => setIsHost(false))
    channelApi.listMessageTags(channelId).then((r: any) => setVocab(r.data)).catch(() => setVocab([]))
  }, [channelId])

  useEffect(() => {
    if (!selectedId) { setDetail(null); return }
    historyApi.session(selectedId, filters).then((r) => setDetail(r.data)).catch(() => setDetail(null))
  }, [selectedId, filters, refreshTick])

  const brow = (
    <PageBrow
      left={<div className="flex items-center gap-2 text-foreground font-semibold"><HistoryIcon size={18} /> Session history</div>}
      onBack={() => navigate(-1)}
    />
  )

  if (denied) {
    return (
      <PageShell brow={brow}>
        <EmptyState icon={<HistoryIcon size={28} />} title="No access" description="Only the channel owner can view this history." />
      </PageShell>
    )
  }

  return (
    <PageShell brow={brow} scroll={false}>
      <ListDetail
        list={<SessionList items={list} loading={loading} selectedId={selectedId} onSelect={(id) => { setParams({ s: id }); setFilters({}) }} />}
        detail={detail ? <SessionDetail detail={detail} filters={filters} onFilters={setFilters} isHost={isHost} vocab={vocab} channelId={channelId} onChanged={() => setRefreshTick((t) => t + 1)} /> : undefined}
        showDetailOnMobile={!!selectedId}
        emptyDetail={<div className="flex h-full items-center justify-center p-8"><EmptyState icon={<HistoryIcon size={28} />} title="Pick a session" description="Choose a session on the left to see its chat, transcript, recordings and participants." /></div>}
      />
    </PageShell>
  )
}
